import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export { formatMalaysianPhone } from "./malaysianPhone"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Convert a string to Title Case (capitalize first letter of each word)
 * Example: "white florals" -> "White Florals"
 */
export function toTitleCase(str: string): string {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
}

/**
 * Format a date string to dd/mm/yyyy format (day first, Malaysian convention)
 * Example: "05/02/2026" for February 5, 2026
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })
}

/**
 * Format a date string to dd/mm/yyyy, h:mm AM/PM format (day first with time)
 * Example: "05/02/2026, 2:30 pm" for February 5, 2026 at 2:30 PM
 */
export function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

/**
 * Format a date string as relative time (e.g., "2 hours ago", "3 days ago")
 */
export function formatRelativeTime(dateString: string): string {
  const now = new Date()
  const date = new Date(dateString)
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (seconds < 60) return "just now"

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`

  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`

  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`

  const years = Math.floor(months / 12)
  return `${years}y ago`
}

/**
 * Long relative time: "Today, 9:51 pm", "Yesterday, 9:51 pm", "2 days ago",
 * "3 months ago", "2 years ago". Calendar days, in the viewer's time zone.
 */
export function formatRelativeDay(
  dateString: string,
  now = new Date()
): string {
  const date = new Date(dateString)
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)
  const time = date
    .toLocaleTimeString("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase()
  const plural = (n: number, unit: string) =>
    `${n} ${unit}${n === 1 ? "" : "s"} ago`

  if (days <= 0) return `Today, ${time}`
  if (days === 1) return `Yesterday, ${time}`
  if (days < 30) return plural(days, "day")
  if (days < 365) return plural(Math.floor(days / 30), "month")
  return plural(Math.floor(days / 365), "year")
}

// 1000 → "1,000", 90.5 → "90.50", 0.667 → "0.667"
export function formatDecimal(n: number) {
  return n.toLocaleString("en-MY", {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 20,
  })
}
